import * as signalR from '@microsoft/signalr'
import useDefaultDevice from 'hooks/useDefaultDevice/useDefaultDevice'
import { useEffect, useCallback, useRef, useMemo } from 'react'
import toast from 'utilities/toast/toast'
import { cfdOnPosSubjects } from '../hwAgentSubjects'
import { CFDClientMessage } from './types'
import { CFDInvokeAction, CFDOnPOSEvent } from './events'
import { ISendToPOSMessage } from 'pages/CustomerFacingDisplayPage/reducer/Interface'
import { CodeResult } from 'views/CustomerFacingDisplay/hardwareAgent/CFDClientEvent'
import crypto from 'crypto-es'
import { useAppSelector } from 'store/useStoreHook'
import Guid from 'utilities/Guid'
import useCFDHubReducer from './useCFDHubReducer'
import useDeviceHardwareAgentCaller from 'hooks/useHardwareAgentCaller/useDeviceHardwareAgentCaller'
import getEnv from 'utilities/getEnv'
import Version from '../version'
import { isNil } from 'lodash-es'
import errorMessages from 'utilities/errorMessages'
import { DeviceStatusResult, DeviceType } from '../Devices/types'
import { Token } from 'types/storeTypes'
import cookies from 'utilities/cookie'
import { defaultSignalRReconnectionDelay } from 'utilities/timeoutHelper'
const LOOP_TIME_SECOND = 10000
const hwAgentLastCompatibilityVersion = getEnv(
    'REACT_APP_HARDWARE_AGENT_LAST_COMPATIBLE_VERSION',
)
const hwAgentLatestVersion = getEnv('REACT_APP_HARDWARE_AGENT_LATEST_VERSION')

/**create the connection id for cfd boardcast */
const connectToCFD = async (hub: signalR.HubConnection, clientId: string) => {
    return await hub.invoke(CFDInvokeAction.ConnectWithCFD, clientId)
}
const acceptPairing = async (
    res: CodeResult,
    hub: signalR.HubConnection,
    clientId: string,
) => {
    res.clientID = clientId
    const connectStatus = await hub.invoke(CFDInvokeAction.AcceptPairing, res)
    return connectStatus
}
const sendMessageToCFS = async (
    message: ISendToPOSMessage,
    hub: signalR.HubConnection,
    clientID: string,
) => {
    const payload: CFDClientMessage = {
        ...message,
        posClientID: clientID,
        messageID: Guid.newGuid().toString(),
        payload: message?.payload ? JSON.stringify(message.payload) : '',
        timestamp: message.timestamp || new Date().toISOString(),
    }

    await hub.invoke(CFDInvokeAction.SendMessageToCFD, payload)
}
const validateTokenPairing = (
    result: CodeResult,
    hub: signalR.HubConnection,
    clinetID: string,
) => {
    const token = cookies.get(Token)
    const hashedToken = crypto.SHA256(token).toString()
    if (result.code === hashedToken) acceptPairing(result, hub, clinetID)
}

function useCFDOnPOSHub() {
    const { state, dispatch } = useCFDHubReducer()
    const { isConnected, isHWAgetOnline: isHwAgentOnline, ...data } = state
    const {
        currentPosTerminal,
        cfdHardwareAgentUri,
        enableCFDOnPosTerminal,
    } = useDefaultDevice()
    const manualRetryConnection = useRef<NodeJS.Timeout | undefined>()

    const enableCFDModule =
        useAppSelector((state) => state.client.currentClient)
            ?.customerDisplaySettingEnable ?? false

    const customerDisplaySettingEnabled = useMemo(
        () => !!enableCFDOnPosTerminal && enableCFDModule,
        [enableCFDModule, enableCFDOnPosTerminal],
    )
    const { getVersionRequest } = useDeviceHardwareAgentCaller(
        cfdHardwareAgentUri,
    )
    const { Disconnected } = signalR.HubConnectionState

    const hubClientRef = useRef<signalR.HubConnection | undefined>()
    const cfdHardwareAgentUriConnected = useRef<string | null>()

    const validateConnection = useCallback(() => {
        if (
            !customerDisplaySettingEnabled ||
            !currentPosTerminal?.clientID ||
            !isHwAgentOnline
        )
            return false

        if (!hubClientRef?.current) return false

        return true
    }, [
        currentPosTerminal?.clientID,
        customerDisplaySettingEnabled,
        isHwAgentOnline,
    ])

    const subscribeCheckWithToken = useCallback(
        (result: CodeResult) => {
            validateTokenPairing(
                result,
                hubClientRef.current!,
                currentPosTerminal?.clientID!,
            )
        },
        [currentPosTerminal?.clientID],
    )

    const invokeSendMessage = useCallback(
        async (message: ISendToPOSMessage) => {
            try {
                await sendMessageToCFS(
                    message,
                    hubClientRef.current!,
                    currentPosTerminal?.clientID!,
                )
            } catch (err) {
                console.error(
                    '[CFD POS] Error invoking SendMessageToCFD: ',
                    err,
                )
                toast.error(
                    '[CFD] Error invoking SendMessageToCFD. See console for details.',
                )
            }
        },
        [currentPosTerminal?.clientID],
    )

    const onSendMessageToCFS = useCallback(
        async (message: ISendToPOSMessage) => {
            dispatch({
                type: 'SET_LATEST_MESSAGE',
                payload: message,
            })
            if (!isConnected || !validateConnection()) return
            await invokeSendMessage(message)
        },
        [dispatch, isConnected, validateConnection, invokeSendMessage],
    )

    const onRegisterClientToCFD = useCallback(async () => {
        const isConnected = await connectToCFD(
            hubClientRef.current!,
            currentPosTerminal?.clientID!,
        )

        if (isConnected) {
            dispatch({ type: 'READY_CONNECT_TO_CFD' })
            return
        }
        dispatch({ type: 'NO_READY_CONNECT_TO_CFD' })
        throw Error('Failed to boardcast to Customer Facing Display.')
    }, [currentPosTerminal?.clientID, dispatch])

    const onAcceptCode = useCallback(
        async (result: CodeResult) => {
            if (!validateConnection()) return
            const connectStatus = await acceptPairing(
                result,
                hubClientRef.current!,
                currentPosTerminal?.clientID!,
            )
            if (connectStatus) dispatch({ type: 'READY_CONNECT_TO_CFD' })
            else {
                dispatch({ type: 'NO_READY_CONNECT_TO_CFD' })
                toast.error('Failed to connect with Customer Facing Display.')
            }
        },
        [currentPosTerminal?.clientID, dispatch, validateConnection],
    )
    const onHWAgentConnected = useCallback(
        async (successMessage?: string) => {
            dispatch({ type: 'HW_AGENT_ONLINE' })
            cfdHardwareAgentUriConnected.current = cfdHardwareAgentUri
            await onRegisterClientToCFD()
            successMessage && toast.success(successMessage)
        },
        [cfdHardwareAgentUri, dispatch, onRegisterClientToCFD],
    )

    const onHWAgentDisconnected = useCallback(
        (showToast: boolean = true) => {
            dispatch({ type: 'HW_AGENT_OFFLINE' })
            showToast && toast.error('CFDHub is offline.')
        },
        [dispatch],
    )

    useEffect(() => {
        const subscription = cfdOnPosSubjects.subscribe((message) => {
            if (!validateConnection()) return

            switch (message.type) {
                case CFDOnPOSEvent.OnCFDRequest:
                    invokeSendMessage(state.latestMessageToCFD)
                    break
                case CFDOnPOSEvent.OnCFDTokenValidate:
                    subscribeCheckWithToken(message.params)
                    break
                default:
                    break
            }
        })

        return () => {
            subscription.unsubscribe()
        }
    }, [
        invokeSendMessage,
        state.latestMessageToCFD,
        subscribeCheckWithToken,
        validateConnection,
    ])

    const connectToHWAgent = useCallback(() => {
        hubClientRef.current = new signalR.HubConnectionBuilder()
            .withUrl(`${cfdHardwareAgentUri}/hubs/pos-client`)
            .configureLogging(signalR.LogLevel.None)
            .withAutomaticReconnect(defaultSignalRReconnectionDelay)
            .build()

        hubClientRef.current.onreconnected((connectionId?: string) => {
            console.debug(
                `[CFD POS] reconnected - connection Id = ${connectionId}`,
            )
            onHWAgentConnected('CFDHub is back to online.')
        })
        hubClientRef.current.onreconnecting((err?: Error) => {
            onHWAgentDisconnected(true)
        })

        // Starts the SignalR connection
        hubClientRef.current
            .start()
            .then(() => {
                if (!hubClientRef.current) return

                console.debug(
                    '[CFD POS] connected - connection Id = ' +
                        hubClientRef.current.connectionId,
                )
                onHWAgentConnected('CFDHub is connected.')

                hubClientRef.current.on(
                    CFDOnPOSEvent.OnCFDRequest,
                    (data: CFDClientMessage) => {
                        console.debug('[CFD POS] OnCFDRequest', data)
                        cfdOnPosSubjects.next({
                            type: CFDOnPOSEvent.OnCFDRequest,
                            params: data,
                        })
                    },
                )
                hubClientRef.current.on(
                    'OnUpdateStatus',
                    (data: DeviceStatusResult) => {
                        console.debug('[CFD POS] OnUpdateStatus', data)
                        if (
                            data.type === DeviceType.CustomerFacingDisplay &&
                            data.isOK
                        ) {
                            toast.success('Customer facing screen is connected')
                        }
                    },
                )
                hubClientRef.current.on(
                    CFDOnPOSEvent.OnCFDCodeValidate,
                    (code: CodeResult) => {
                        console.debug('[CFD POS] OnCFDCodeValidate', code)
                        cfdOnPosSubjects.next({
                            type: CFDOnPOSEvent.OnCFDCodeValidate,
                            params: code,
                        })
                    },
                )
                hubClientRef.current.on(
                    CFDOnPOSEvent.OnCFDTokenValidate,
                    (code: CodeResult) => {
                        console.debug('[CFD POS] OnCFDTokenValidate', code)
                        cfdOnPosSubjects.next({
                            type: CFDOnPOSEvent.OnCFDTokenValidate,
                            params: code,
                        })
                    },
                )
            })
            .catch((e) => {
                // this will prevent sending an error log to Sentry, when HWAgent is offline
                // log here if needed...

                // fire disconnected in-case cannot connect to HW Agent at all
                // this will help syncing the HWAgent status to Cloud API
                onHWAgentDisconnected(false)
            })
    }, [cfdHardwareAgentUri, onHWAgentConnected, onHWAgentDisconnected])
    useEffect(() => {
        if (
            customerDisplaySettingEnabled &&
            (cfdHardwareAgentUri !== cfdHardwareAgentUriConnected.current ||
                !hubClientRef.current)
        ) {
            connectToHWAgent()
        }

        return () => {
            console.debug('[CFD POS] clean up usePOSClientHub')
            onHWAgentDisconnected(false)
            hubClientRef.current?.stop()
            hubClientRef.current = undefined
        }
    }, [
        cfdHardwareAgentUri,
        connectToHWAgent,
        customerDisplaySettingEnabled,
        onHWAgentConnected,
        onHWAgentDisconnected,
    ])
    const cleanLoopRetry = useCallback(() => {
        if (manualRetryConnection.current)
            clearInterval(manualRetryConnection.current)
    }, [])
    const loopRetryProcess = useCallback(
        (func: () => void) => {
            cleanLoopRetry()
            const attemptToStartConnection = () => {
                func()
            }
            manualRetryConnection.current = setInterval(
                () => attemptToStartConnection(),
                LOOP_TIME_SECOND,
            )
        },
        [cleanLoopRetry],
    )

    useEffect(() => {
        if (!customerDisplaySettingEnabled) return
        // Manually attempt to reconnect the signalR in case of losing connection whether due to the absence of
        // the HWAgent or the terminal itself is disconnected from the network
        if (!hubClientRef) {
            loopRetryProcess(connectToHWAgent)
        } else if (
            hubClientRef.current &&
            hubClientRef.current.state === Disconnected
        ) {
            loopRetryProcess(() => {
                hubClientRef
                    .current!.start()
                    .then(() => onHWAgentConnected())
                    .catch(() => {
                        // this will prevent sending this error log to Sentry, when HWAgent is offline
                        // log here if needed
                    })
            })
        }

        return () => {
            // clean up the setTimeout when this is unmounted
            cleanLoopRetry()
        }
    }, [
        Disconnected,
        cleanLoopRetry,
        connectToHWAgent,
        customerDisplaySettingEnabled,
        loopRetryProcess,
        onHWAgentConnected,
    ])

    const checkHWAgentVersion = useCallback(async () => {
        try {
            const hwAgentVersion = await getVersionRequest()
            dispatch({
                type: 'SET_HW_AGENT_VERSION',
                payload: {
                    version: hwAgentVersion?.data.version,
                    compatibilityVersion: hwAgentLastCompatibilityVersion,
                    latestVersion: hwAgentLatestVersion,
                },
            })
            // Check if the hardware agent is compatible with the current version
            if (
                isNil(hwAgentVersion?.data?.version) ||
                isNil(hwAgentLastCompatibilityVersion) ||
                isNil(hwAgentLatestVersion)
            )
                return

            const version = new Version(hwAgentVersion?.data?.version ?? '')
            dispatch({
                type: 'SET_HW_AGENT_VERSION_STATUS',
                payload: {
                    isUncompatibleVersion: version.isOlder(
                        hwAgentLastCompatibilityVersion,
                    ),
                    isUnderLatestVersion: version.isOlder(hwAgentLatestVersion),
                },
            })
        } catch (err) {
            let { message, arg } = errorMessages(
                err,
                'Unable connect get CFD HwAgent version',
            )
            toast.error(message, arg)
        }
    }, [dispatch, getVersionRequest])

    useEffect(() => {
        if (isHwAgentOnline) {
            checkHWAgentVersion()
        }
    }, [checkHWAgentVersion, isHwAgentOnline])

    return {
        ...data,
        isConnected,
        onAcceptCode,
        onSendMessageToCFS,
        useCFD: customerDisplaySettingEnabled,
        isHwAgentOnline,
    }
}

export default useCFDOnPOSHub
